import * as React from "react";
import {api} from "../../api/api";
import IndividualContact from "../../model/contact/IndividualContact";
import Toast from "../helper/Toast";
import Phone from "../../model/phone/Phone";
import ButtonPrimary from "../../hiberUiKit/src/atoms/Buttons/ButtonPrimary";
import {connect} from "react-redux";
import {RootState} from "../../store/reducer/root/RootState";
import User from "../../model/user/User";
import {RouteComponentProps, withRouter} from "react-router";
import InlineRadioButtons from "../ui/input/InlineRadioButtons";
import {isNullOrUndefined} from "util";

declare var Twilio: any;

interface IComponentProps {
	/**
	 * Contact to call.
	 */
	contact: IndividualContact;
}

interface IReduxProps {
	loggedUser: User
}

enum OutgoingCallStatus {
	None = "None",
	Ready = "Ready",
	Offline = "Offline",
	InProgress = "InProgress",
	Error = "Error"
}

interface IState {
	/**
	 * Current state of outgoing call.
	 */
	outgoingCallState: OutgoingCallStatus;
	/**
	 * Error message to display if state is "Errror"
	 */
	outgoingCallError: string;
	/**
	 * Phone for which make call.
	 */
	selectedPhoneNumber: Phone;
}

interface IRoute {
	leadId: string
}

type Props = IComponentProps & IReduxProps & RouteComponentProps<IRoute>;

class TwillioDialer extends React.PureComponent<Props, IState> {
	/**
	 * Outgoing call connection.
	 * If null, there is no call in progress.
	 */
	outgoingCallConnection: any = null;

	/**
	 * Twilio device instance
	 */
	device: any = null;

	constructor(props: Props) {
		super(props);
		const phoneNumbers = props.contact.data.communication.phoneNumbers.getOrElse([]);
		this.state = {
			outgoingCallState: OutgoingCallStatus.None,
			outgoingCallError: "",
			selectedPhoneNumber: phoneNumbers[phoneNumbers.length - 1]
		};
		this.device = new Twilio.Device();
		this.makeCall = this.makeCall.bind(this);
		this.endCall = this.endCall.bind(this);
		this.connectToTwilio = this.connectToTwilio.bind(this);
		this.onPhoneChange = this.onPhoneChange.bind(this);
	}

	componentWillUnmount() {
		this.endCall();
		// TODO: Figure out why calling destroy throws errors on console, probably twilio SDK issue.
		// this.device.destroy();
	}

	componentDidMount() {
		this.connectToTwilio();
		this.device.on("disconnect", () => {
			this.setState({outgoingCallError: "", outgoingCallState: OutgoingCallStatus.Ready});
			this.outgoingCallConnection = null;
		});
		this.device.on("ready", () => {
			this.setState({outgoingCallError: "", outgoingCallState: OutgoingCallStatus.Ready});
		});
		this.device.on("offline", () => {
			this.setState({outgoingCallError: "", outgoingCallState: OutgoingCallStatus.Offline});
		});
		this.device.on("error", (error: any) => {
			this.setState({outgoingCallError: error.message, outgoingCallState: OutgoingCallStatus.Error});
		});
	}

	render() {

		return (
			<div className="dialer">
				<label>Select a number to call:</label>
				{this.renderPhoneNumbers()}
				<div className="cta">
					<div>
						<div>Status: <b>{this.statusToText()}</b></div>
						{this.renderError()}
					</div>
					<div style={{alignSelf: "flex-end"}}>
						{this.state.outgoingCallState == OutgoingCallStatus.Offline && this.outgoingCallConnection == null &&
						<ButtonPrimary onClick={this.connectToTwilio} label={"Reconnect"} variant={"warning"}/>
						}
						<ButtonPrimary onClick={this.endCall}
									   label={"End call"}
									   variant={"danger"}
									   disabled={this.state.outgoingCallState != OutgoingCallStatus.InProgress}/>
						<ButtonPrimary onClick={this.makeCall}
									   label={"Call"}
									   variant={"success"}
									   disabled={this.state.outgoingCallState != OutgoingCallStatus.Ready}/>
					</div>
				</div>
			</div>
		);
	}

	onPhoneChange(value: string) {
		const phone = this.props.contact.data.communication.phoneNumbers.getOrElse([]).filter(
			p => p.internationalPhoneNumber == value)[0];
		if (!isNullOrUndefined(phone)) {
			this.setState({selectedPhoneNumber: phone});
		}
	}

	renderPhoneNumbers() {
		const radioButtons = this.props.contact.data.communication.phoneNumbers.getOrElse([]).map(phone => {
			return {
				label: phone.nationalPhoneNumber,
				value: phone.internationalPhoneNumber,
				disabled: this.state.outgoingCallState !== OutgoingCallStatus.Ready
			}
		});
		return (
			<InlineRadioButtons radioButtonOptions={radioButtons}
								onChange={this.onPhoneChange}
								selected={this.state.selectedPhoneNumber.internationalPhoneNumber}/>
		)
	}

	/**
	 * Map current status of the call to more user friendly text.
	 * @returns {string}
	 */
	statusToText(): string {
		switch (this.state.outgoingCallState) {
			case OutgoingCallStatus.Error:
				return "Error";
			case OutgoingCallStatus.Ready:
				return "Ready to call";
			case OutgoingCallStatus.InProgress:
				return "Calling...";
			case OutgoingCallStatus.Offline:
				return "Connection to Twilio lost, please reconnect";
			case OutgoingCallStatus.None:
				return "Initializing...";
			default:
				return "Unknown";
		}
	}

	/**
	 * Map current twilio device status to OutgoingCallStatus enum element.
	 * @returns {OutgoingCallStatus}
	 */
	getOutgoingCallStatusFromDeviceStatus(): OutgoingCallStatus {
		if (this.device.instance != null) {
			switch (this.device.status()) {
				case "ready":
					return OutgoingCallStatus.Ready;
				case "offline":
					return OutgoingCallStatus.Offline;
				case "busy":
					return OutgoingCallStatus.InProgress;
				default:
					return OutgoingCallStatus.None;
			}
		}
		return OutgoingCallStatus.None;
	}

	renderError() {
		if (this.state.outgoingCallState !== OutgoingCallStatus.Error)
			return;

		return (
			<span className="t-body is-error">Error message: <b>{this.state.outgoingCallError}</b></span>
		)
	}

	makeCall() {
		this.outgoingCallConnection = this.device.connect({
			To: this.state.selectedPhoneNumber.internationalPhoneNumber,
			hiberCustomerId: this.props.contact.id,
			hiberUserId: this.props.loggedUser.id,
			hiberLeadId: this.props.match.params.leadId
		});
		this.outgoingCallConnection.error((error: any) => {
			this.setState({outgoingCallError: error.message, outgoingCallState: OutgoingCallStatus.Error});
			this.endCall();
		});
		this.setState({outgoingCallError: "", outgoingCallState: OutgoingCallStatus.InProgress});
	}

	endCall() {
		if (this.outgoingCallConnection) {
			this.outgoingCallConnection.disconnect();
			this.outgoingCallConnection = null;
		}
	}

	connectToTwilio() {
		api.instance.twilioApi.fetchToken().then(token => {
			try {
				this.device.setup(token);
				this.setState({outgoingCallState: this.getOutgoingCallStatusFromDeviceStatus()});
			}
			catch (error) {
				this.setState({outgoingCallError: error.message, outgoingCallState: OutgoingCallStatus.Error});
			}
		}).catch(error => {
			if (error && error.response && error.response.status === 412) {
				const msg = "Twilio voice is not enabled, please contact administrator";
				this.setState({
					outgoingCallError: msg,
					outgoingCallState: OutgoingCallStatus.Error
				});
				Toast.error(msg, "Twilio voice");
			}
			else {
				const msg = "An unexpected error occurred, please contact administrator";
				this.setState({
					outgoingCallError: msg,
					outgoingCallState: OutgoingCallStatus.Error
				});
				Toast.error(msg, "Twilio voice");
			}
		});
	}
}

function mapStateToProps(state: RootState) {
	return {
		loggedUser: state.user.user.get
	}
}

export default withRouter<IComponentProps>(connect(mapStateToProps)(TwillioDialer));